// src/components/EquipmentManager/EquipmentDetailsModal.js

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styles from './EquipmentDetailsModal.module.css';
import AssignEquipmentModal from './AssignEquipmentModal';
import ReviewsTab from './ReviewsTab';
import OverviewTab from './OverviewTab';
import SpecsTab from './SpecsTab';
import AssignmentsTab from './AssignmentsTab';
import StarRating from '../common/StarRating';
import { 
  FaTimes, 
  FaBox, 
  FaShoppingCart,
  FaBoxOpen,
  FaExternalLinkAlt,
  FaChevronLeft,
  FaChevronRight,
  FaInfoCircle,
  FaStar,
  FaListAlt,
  FaUsers
} from 'react-icons/fa';
import { useUser } from '../../context/UserContext';

const EquipmentDetailsModal = ({ 
  equipment, 
  onClose, 
  programId,
  canManage = false,
  onAssign
}) => {
  const { userProfile } = useUser();
  const [activeTab, setActiveTab] = useState('overview');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  
  const images = equipment.images || (equipment.imageUrl ? [{ url: equipment.imageUrl, isPrimary: true }] : []);
  const currentImage = images[currentImageIndex] || { url: '/default-equipment.png' };
  
  const handlePrevImage = () => {
    setCurrentImageIndex(prev => (prev > 0 ? prev - 1 : images.length - 1));
  };
  
  const handleNextImage = () => {
    setCurrentImageIndex(prev => (prev < images.length - 1 ? prev + 1 : 0));
  };
  
  const handleBuyClick = (linkType) => {
    // Track click
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sports-equipments/${equipment._id}/track-click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ linkType })
    }).catch(err => console.warn('Failed to track click:', err));
  };
  
  const handleAssignClick = () => {
    setIsAssignModalOpen(true);
  };

  const handleOverlayClick = (e) => {
    // Don't close if assign modal is open
    if (isAssignModalOpen) return;
    onClose(e);
  };
  
  return createPortal(
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerLeft}>
            <h2 className={styles.modalTitle}>{equipment.equipmentName}</h2>
            {equipment.sponsorInfo?.name && (
              <span className={styles.sponsorName}>by {equipment.sponsorInfo.name}</span>
            )}
          </div>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>
        
        <div className={styles.modalBody}>
          {/* Top Section: Image Gallery + Quick Info */}
          <div className={styles.topSection}>
            {/* Image Gallery */}
            <div className={styles.imageGallery}>
              <div className={styles.mainImageContainer}>
                <img 
                  src={currentImage.url} 
                  alt={currentImage.alt || equipment.equipmentName}
                  className={styles.mainImage}
                />
                
                {/* Navigation Arrows */}
                {images.length > 1 && (
                  <>
                    <button 
                      onClick={handlePrevImage}
                      className={styles.prevBtn}
                      aria-label="Previous image"
                    >
                      <FaChevronLeft />
                    </button>
                    <button 
                      onClick={handleNextImage}
                      className={styles.nextBtn}
                      aria-label="Next image"
                    >
                      <FaChevronRight />
                    </button>
                    
                    {/* Image Dots */}
                    <div className={styles.imageDots}>
                      {images.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentImageIndex(idx)}
                          className={idx === currentImageIndex ? styles.dotActive : styles.dot}
                          aria-label={`Go to image ${idx + 1}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {/* Quick Info */}
            <div className={styles.quickInfo}>
              {/* Rating */}
              {equipment.ratingSummary && equipment.ratingSummary.totalReviews > 0 && (
                <div className={styles.ratingSection}>
                  <StarRating 
                    rating={equipment.ratingSummary.averageRating} 
                    size="large"
                    showNumber={true}
                  />
                  <span className={styles.reviewCount}>
                    {equipment.ratingSummary.totalReviews} review{equipment.ratingSummary.totalReviews !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
              
              {/* Price */}
              {equipment.averagePrice && (
                <div className={styles.priceSection}>
                  <span className={styles.priceLabel}>Price:</span>
                  <span className={styles.priceValue}>${equipment.averagePrice}</span>
                </div>
              )}
              
              {/* Category & Condition */}
              <div className={styles.metaInfo}>
                <div className={styles.metaItem}>
                  <FaBox /> 
                  {/* Display category - use categoryName fallback if category is ObjectId */}
                  {equipment.category?.name ? (
                    // New format with populated category
                    <span>{equipment.category.icon} {equipment.category.name}</span>
                  ) : equipment.categoryName ? (
                    // Fallback to categoryName field
                    <span>{equipment.categoryName}</span>
                  ) : typeof equipment.category === 'string' && equipment.category.length < 30 ? (
                    // Old format with category string
                    <span>{equipment.category}</span>
                  ) : (
                    // Last resort
                    <span>N/A</span>
                  )}
                </div>
                <div className={styles.metaItem}>
                  <FaInfoCircle /> Condition: {equipment.condition || 'Good'}
                </div>
              </div>
              
              {/* Purchase Links */}
              {(equipment.purchaseLinks?.amazon || equipment.purchaseLinks?.directStore) && (
                <div className={styles.purchaseLinks}>
                  {equipment.purchaseLinks?.amazon && (
                    <a 
                      href={equipment.purchaseLinks.amazon}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleBuyClick('amazon')}
                      className={styles.buyBtn}
                    >
                      <FaShoppingCart /> Buy on Amazon
                    </a>
                  )}
                  
                  {equipment.purchaseLinks?.directStore && (
                    <a 
                      href={equipment.purchaseLinks.directStore}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleBuyClick('direct')}
                      className={styles.buyBtn}
                    >
                      <FaExternalLinkAlt /> Buy Direct
                    </a>
                  )}
                </div>
              )}
              
              {/* Assign Button */}
              {canManage && (
                <button 
                  onClick={handleAssignClick}
                  className={styles.assignBtn}
                >
                  <FaBoxOpen /> Assign to Player
                </button>
              )}
            </div>
          </div>
          
          {/* Tabs Section */}
          <div className={styles.tabsSection}>
            <div className={styles.tabButtons}>
              <button 
                className={activeTab === 'overview' ? styles.activeTab : styles.tab}
                onClick={() => setActiveTab('overview')}
              >
                <FaInfoCircle /> Overview
              </button>
              <button 
                className={activeTab === 'reviews' ? styles.activeTab : styles.tab}
                onClick={() => setActiveTab('reviews')}
              >
                <FaStar /> Reviews ({equipment.ratingSummary?.totalReviews || 0})
              </button>
              <button 
                className={activeTab === 'specs' ? styles.activeTab : styles.tab}
                onClick={() => setActiveTab('specs')}
              >
                <FaListAlt /> Specifications
              </button>
              {canManage && (
                <button 
                  className={activeTab === 'assignments' ? styles.activeTab : styles.tab}
                  onClick={() => setActiveTab('assignments')}
                >
                  <FaUsers /> Assignments
                </button>
              )}
            </div>
            
            <div className={styles.tabContent}>
              {activeTab === 'overview' && <OverviewTab equipment={equipment} />}
              {activeTab === 'reviews' && (
                <ReviewsTab 
                  equipment={equipment}
                  currentUserId={userProfile?._id}
                  canManage={canManage}
                />
              )}
              {activeTab === 'specs' && <SpecsTab equipment={equipment} />}
              {activeTab === 'assignments' && canManage && (
                <AssignmentsTab equipment={equipment} programId={programId} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Assign Equipment Modal */}
      {isAssignModalOpen && (
        <AssignEquipmentModal
          isOpen={isAssignModalOpen}
          onClose={() => setIsAssignModalOpen(false)}
          programId={programId}
          onAssignmentUpdated={() => {
            setIsAssignModalOpen(false);
            // Could refresh equipment data here if needed
          }}
          preSelectedEquipment={equipment}
        />
      )}
    </div>,
    document.body
  );
};

EquipmentDetailsModal.propTypes = {
  equipment: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  programId: PropTypes.string.isRequired,
  canManage: PropTypes.bool,
  onAssign: PropTypes.func
};

export default EquipmentDetailsModal;
